import { useState, useCallback, useMemo } from "react";
import { priceInsightService } from "@/lib/services/priceInsightService";
import { debounce } from "@/lib/utils/debounce";
import type {
  AdminPriceInsight,
  AdminPriceInsightState,
  PriceSuggestion,
  PriceInsightState,
} from "@/types/price-insight";
import { logPricingInsightMetric } from "@/lib/services/pricingInsightMetrics";
import { usePricingInsightFlagsContext } from "@/contexts/PricingInsightFlagsContext";
import { dispatchPricingInsightFlagsInvalidate } from "@/lib/pricingInsightFlagsClient";
import type { PricingDataSource } from "@/lib/pricing/pricingDataSource";
import { listingAttributesCacheSegment } from "@/lib/pricing/pricingQueryIdentity";
import { clearAdminSellerBandCache } from "@/lib/hooks/useAdminSellerBandPreview";

// Cache for price suggestions (not feature flags)
const priceCache = new Map<string, PriceSuggestion | AdminPriceInsight>();

/** Dev toolbox: clear in-memory suggestion cache without touching feature flags alone. */
export function clearPriceInsightSuggestionCache(): void {
  priceCache.clear();
}

const getCacheKey = (
  title: string,
  category: string,
  type: string,
  price?: number,
  listingAttributes?: Record<string, unknown> | null,
) => {
  return `${title}-${category}-${type}-${price || 0}${listingAttributesCacheSegment(listingAttributes)}`;
};

/** Avoid collisions with user price cache (same title/category can differ by API). */
export const getAdminPriceInsightCacheKey = (
  title: string,
  category: string,
  type: string,
  price?: number,
  dataSource?: PricingDataSource,
  aiProvider?: string,
  itemId?: string,
  listingAttributes?: Record<string, unknown> | null,
) =>
  `admin|${getCacheKey(title, category, type, price, listingAttributes)}|${dataSource ?? ""}|${aiProvider ?? ""}|${itemId ?? ""}`;

const getAdminCacheKey = getAdminPriceInsightCacheKey;

interface PriceInsightRequestOptions {
  itemId?: string;
  dataSource?: PricingDataSource;
  aiProvider?: "genkit" | "openai" | "none";
  signal?: AbortSignal;
  listingAttributes?: Record<string, unknown> | null;
}

export function peekAdminPriceInsightCache(
  title: string,
  category: string,
  type: string,
  currentPrice?: number,
  options?: PriceInsightRequestOptions,
): AdminPriceInsight | null {
  if (!title.trim() || !category.trim()) {
    return null;
  }
  const cacheKey = getAdminCacheKey(
    title,
    category,
    type,
    currentPrice,
    options?.dataSource,
    options?.aiProvider,
    options?.itemId,
    options?.listingAttributes,
  );
  return (priceCache.get(cacheKey) as AdminPriceInsight | undefined) ?? null;
}

/**
 * Fetch admin pricing insight for a listing — uses shared in-memory cache.
 * Callers (detail panel, review modal prefill) share the same cache key.
 */
export async function fetchAdminPriceInsightForListing(
  title: string,
  category: string,
  type: string,
  currentPrice?: number,
  options?: PriceInsightRequestOptions & { forceRefresh?: boolean },
): Promise<AdminPriceInsight> {
  const cacheKey = getAdminCacheKey(
    title,
    category,
    type,
    currentPrice,
    options?.dataSource,
    options?.aiProvider,
    options?.itemId,
    options?.listingAttributes,
  );

  if (options?.forceRefresh) {
    priceCache.delete(cacheKey);
  } else {
    const cached = priceCache.get(cacheKey) as AdminPriceInsight | undefined;
    if (cached) {
      return cached;
    }
  }

  const result = await priceInsightService.getAdminPriceSuggestion(
    title,
    category,
    type,
    {
      dataSource: options?.dataSource,
      aiProvider: options?.aiProvider,
      excludeItemId: options?.itemId,
      listingAttributes: options?.listingAttributes,
      signal: options?.signal,
    },
  );

  priceCache.set(cacheKey, result);
  return result;
}

export function clearAdminPriceInsightCacheEntry(
  title: string,
  category: string,
  type: string,
  currentPrice?: number,
  options?: PriceInsightRequestOptions,
): void {
  const cacheKey = getAdminCacheKey(
    title,
    category,
    type,
    currentPrice,
    options?.dataSource,
    options?.aiProvider,
    options?.itemId,
    options?.listingAttributes,
  );
  priceCache.delete(cacheKey);
}

/** Drop cached reviewer AI rows for a listing fingerprint (all sources/providers). */
export function invalidateAdminPriceInsightCacheForListing(params: {
  title: string;
  category: string;
  type: string;
}): void {
  const title = params.title.trim();
  const category = params.category.trim();
  const type = params.type;
  const marker = `${title}-${category}-${type}-`;

  for (const key of priceCache.keys()) {
    if (key.startsWith("admin|") && key.includes(marker)) {
      priceCache.delete(key);
    }
  }
}

const computeAvailability = (
  flags: {
    global_pricing_insight_enabled: boolean;
    pricing_insight_enabled: boolean;
    user_pricing_insight_enabled: boolean;
  } | null,
  target: "admin" | "user",
) => {
  if (!flags) return false;
  if (target === "admin") {
    return (
      flags.global_pricing_insight_enabled && flags.pricing_insight_enabled
    );
  }
  return (
    flags.global_pricing_insight_enabled && flags.user_pricing_insight_enabled
  );
};

export function usePricingInsightAvailability(
  target: "admin" | "user" = "admin",
) {
  const ctx = usePricingInsightFlagsContext();
  const isEnabled = ctx.flags ? computeAvailability(ctx.flags, target) : false;

  return {
    isEnabled,
    isLoading: ctx.isLoading,
    error: ctx.error,
    refresh: () => ctx.refresh(true),
  };
}

export function usePriceInsight() {
  const { isUserPricingInsightEnabled: isEnabled } =
    usePricingInsightFlagsContext();

  const [state, setState] = useState<PriceInsightState>({
    isLoading: false,
    error: null,
    errorDetails: null,
    data: null,
  });

  const getPriceSuggestion = useCallback(
    async (
      title: string,
      category: string,
      type: string,
      currentPrice?: number,
      options?: { listingAttributes?: Record<string, unknown> | null },
    ) => {
      if (!isEnabled) return;

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const cacheKey = getCacheKey(
          title,
          category,
          type,
          currentPrice,
          options?.listingAttributes,
        );
        const cachedResult = priceCache.get(cacheKey);

        if (cachedResult) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            data: cachedResult,
          }));
          return;
        }

        const result = await priceInsightService.getPriceSuggestion(
          title,
          category,
          type,
          currentPrice,
          { listingAttributes: options?.listingAttributes },
        );

        priceCache.set(cacheKey, result);

        setState((prev) => ({
          ...prev,
          isLoading: false,
          data: result,
        }));
      } catch (error) {
        console.error("Error getting price suggestion:", error);

        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";

        let userFriendlyError =
          "Price insight is currently unavailable. You can continue with your listing.";

        if (
          errorMessage.includes("Price insight is not available") ||
          errorMessage.includes("Price insight is currently unavailable") ||
          errorMessage.includes("continue with your listing")
        ) {
          userFriendlyError = errorMessage;
        }

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: userFriendlyError,
          errorDetails: null,
        }));
      }
    },
    [isEnabled],
  );

  const debouncedGetPriceSuggestion = useMemo(
    () => debounce(getPriceSuggestion as (...args: unknown[]) => unknown, 500),
    [getPriceSuggestion],
  );

  const clearSuggestion = useCallback(() => {
    setState((prev) => ({
      ...prev,
      data: null,
      error: null,
      errorDetails: null,
    }));
  }, []);

  return {
    isEnabled,
    isLoading: state.isLoading,
    error: state.error,
    errorDetails: state.errorDetails,
    data: state.data,
    getPriceSuggestion: debouncedGetPriceSuggestion,
    clearSuggestion,
  };
}

export function useAdminPriceInsight() {
  const { isAdminPricingInsightEnabled: isEnabled } =
    usePricingInsightFlagsContext();

  const [state, setState] = useState<AdminPriceInsightState>({
    isLoading: false,
    error: null,
    errorDetails: null,
    data: null,
  });

  const fetchPriceSuggestion = useCallback(
    async (
      title: string,
      category: string,
      type: string,
      currentPrice?: number,
      options?: PriceInsightRequestOptions & { forceRefresh?: boolean },
    ) => {
      if (!isEnabled) return;

      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const metadata = {
        title,
        category,
        type,
        currentPrice,
      };

      const startedAt = performance.now();

      void logPricingInsightMetric({
        context: "admin_price_insight",
        eventType: "request",
        status: "pending",
        itemId: options?.itemId,
        itemTitle: title,
        itemCategory: category,
        metadata,
      });

      try {
        const hadCache = Boolean(
          peekAdminPriceInsightCache(
            title,
            category,
            type,
            currentPrice,
            options,
          ),
        );

        const result = await fetchAdminPriceInsightForListing(
          title,
          category,
          type,
          currentPrice,
          options,
        );

        setState((prev) => ({
          ...prev,
          isLoading: false,
          data: result,
        }));

        void logPricingInsightMetric({
          context: "admin_price_insight",
          eventType: "response",
          status: "success",
          itemId: options?.itemId,
          latencyMs: Math.round(performance.now() - startedAt),
          itemTitle: title,
          itemCategory: category,
          metadata: {
            ...metadata,
            cached: hadCache,
            suggestedPrice: result.suggestedPrice,
          },
        });
      } catch (error) {
        console.error(
          "[Admin Price Insight] Error getting price suggestion:",
          error,
        );
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          errorDetails: null,
        }));

        void logPricingInsightMetric({
          context: "admin_price_insight",
          eventType: "response",
          status: "error",
          itemId: options?.itemId,
          latencyMs: Math.round(performance.now() - startedAt),
          itemTitle: title,
          itemCategory: category,
          errorMessage:
            error instanceof Error ? error.message : "Unknown error",
          metadata,
        });
      }
    },
    [isEnabled],
  );

  const debouncedGetPriceSuggestion = useMemo(
    () =>
      debounce(fetchPriceSuggestion as (...args: unknown[]) => unknown, 500),
    [fetchPriceSuggestion],
  );

  const hydrateFromCache = useCallback(
    (
      title: string,
      category: string,
      type: string,
      currentPrice?: number,
      options?: PriceInsightRequestOptions,
    ) => {
      const cached = peekAdminPriceInsightCache(
        title,
        category,
        type,
        currentPrice,
        options,
      );
      if (!cached) {
        return false;
      }
      setState({
        isLoading: false,
        error: null,
        errorDetails: null,
        data: cached,
      });
      return true;
    },
    [],
  );

  return {
    isEnabled,
    isLoading: state.isLoading,
    error: state.error,
    errorDetails: state.errorDetails,
    data: state.data,
    getPriceSuggestion: debouncedGetPriceSuggestion,
    fetchPriceSuggestion,
    hydrateFromCache,
  };
}

/**
 * Call after admin saves pricing insight toggles so all tabs refresh flags (SWR revalidate).
 * Prefer `dispatchPricingInsightFlagsInvalidate` from `@/lib/pricingInsightFlagsClient`.
 */
export function invalidatePricingInsightSettingsCache() {
  dispatchPricingInsightFlagsInvalidate();
  clearPriceInsightSuggestionCache();
  clearAdminSellerBandCache();
}
